import {
  EllipsisOutlined,
  FilterFilled,
  PlusOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Dropdown, Input, Select, Space } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AccessControl from '../../AccessControl';
import {
  APP_BUILD_ENVIRONMENTS,
  APP_BUILD_REVIEW_STATUS,
  APP_BUILDS_TABLE_FIELDS,
  APP_TYPES,
  DEFAULT_DATE_FORMAT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROUTES_MODULES_KEY,
  SORT_BY,
} from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import { PROJECT_FOR_RELEASE } from '../releases/graphql/Queries';
import './appBuild.less';
import StatusBadge from './components/StatusBadge';
import { APP_BUILD_STATUS_UPDATE } from './graphql/Mutations';
import { APP_BUILDS, APP_BUILD_ASSIGNEES } from './graphql/Queries';

const buildAppBuildsFilter = (params) => ({
  appName: params?.appName?.length ? params?.appName : undefined,
  appType: params?.appType?.length ? params?.appType : undefined,
  environment: params?.environment?.length ? params?.environment : undefined,
  assigneeId: params?.assignee?.length ? params?.assignee : undefined,
  androidStatus: params?.androidStatus?.length
    ? params?.androidStatus
    : undefined,
  iosStatus: params?.iosStatus?.length ? params?.iosStatus : undefined,
  requestNumber: params?.requestNumber || undefined,
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  sortField: params?.sortField,
  sortOrder: params?.sortOrder,
});

function AppBuilds(props) {
  const { location: { state } = {} } = props;
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: APP_BUILDS_TABLE_FIELDS,
  });
  const [appNameSearch, setAppNameSearch] = useState('');
  const [designationDataSearch, setDesignationDataSearch] = useState('');
  // Draft values for the Apply/Reset column filter popups — only committed
  // to the URL (and re-fetched) once "Apply" is clicked.
  const [draftFilter, setDraftFilter] = useState({
    requestNumber: params?.requestNumber,
    appName: state?.projectName ? [state?.projectName] : params?.appName,
    appType: params?.appType,
    environment: params?.environment,
    assignee: params?.assignee,
    androidStatus: params?.androidStatus,
    iosStatus: params?.iosStatus,
  });
  const [filterVisible, setFilterVisible] = useState({
    appNameFilterVisible: false,
    appTypeFilterVisible: false,
    environmentFilterVisible: false,
    assigneeFilterVisible: false,
    androidStatusFilterVisible: false,
    iosStatusFilterVisible: false,
    requestNumberFilterVisible: false,
  });

  const [executeQuery, { data, loading }] = useLazyQuery(APP_BUILDS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  useEffect(() => {
    if (state?.projectName) {
      setParams({ appName: [state?.projectName], page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    executeQuery({ variables: { filters: buildAppBuildsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // debounce must be created once, not on every render — otherwise each
  // keystroke gets its own independent timer instead of resetting a shared
  // one, so rapid typing fires multiple out-of-order searches that corrupt
  // CommonSelect's incremental list/pagination state (breaks search + scroll)
  const handleAppNameSearch = useCallback(
    debounce((value) => setAppNameSearch(value), 500),
    [],
  );

  const handleDesignationSearch = useCallback(
    debounce((value) => setDesignationDataSearch(value), 500),
    [],
  );

  const handleAppName = (value) => {
    setDraftFilter((prev) => ({
      ...prev,
      appName: value?.includes('All apps') ? [] : value,
    }));
    setAppNameSearch('');
  };

  const handleAppType = (value) => {
    setDraftFilter((prev) => ({ ...prev, appType: value || [] }));
  };

  const handleEnvironment = (value) => {
    setDraftFilter((prev) => ({ ...prev, environment: value || [] }));
  };

  const handleAssignee = (value) => {
    setDraftFilter((prev) => ({ ...prev, assignee: value || [] }));
  };

  const handleAndroidStatus = (value) => {
    setDraftFilter((prev) => ({ ...prev, androidStatus: value || [] }));
  };

  const handleIosStatus = (value) => {
    setDraftFilter((prev) => ({ ...prev, iosStatus: value || [] }));
  };

  const handleRequestNumber = (e) => {
    setDraftFilter((prev) => ({
      ...prev,
      requestNumber: e?.target?.value || '',
    }));
  };

  const handleReset = (prop) => {
    const patch = { [prop]: prop === 'requestNumber' ? '' : [] };
    setDraftFilter((prev) => ({ ...prev, ...patch }));
    setParams({ ...patch, page: 1 });
    setFilterVisible((prev) => ({
      ...prev,
      [`${prop}FilterVisible`]: false,
    }));
  };

  const handleFilter = () => {
    setParams({ ...draftFilter, page: 1 });
    setFilterVisible({
      appNameFilterVisible: false,
      appTypeFilterVisible: false,
      environmentFilterVisible: false,
      assigneeFilterVisible: false,
      androidStatusFilterVisible: false,
      iosStatusFilterVisible: false,
      requestNumberFilterVisible: false,
    });
  };

  const getRequestNumberFilterProps = () => ({
    filterDropdownOpen: filterVisible?.requestNumberFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        requestNumberFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={
          params?.requestNumber?.length > 0 ? 'filter-filled-data' : ''
        }
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Input
            placeholder="Search Request Number"
            value={draftFilter?.requestNumber}
            onChange={handleRequestNumber}
            onPressEnter={handleFilter}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReset('requestNumber')}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getAppNameFilterProps = () => ({
    filterDropdownOpen: filterVisible?.appNameFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        appNameFilterVisible: visible,
      }),
    filterSearch: true,
    filterIcon: () => (
      <FilterFilled
        className={params?.appName?.length > 0 ? 'filter-filled-data' : ''}
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <CommonSelect
            mode="multiple"
            placeholder="Select App Name"
            showSearch
            query={PROJECT_FOR_RELEASE}
            customOptions={[{ name: 'All apps' }]}
            variables={{
              filters: {
                searchString: appNameSearch,
                sortField: 'createdOn',
                sortOrder: SORT_BY.DESC,
              },
            }}
            onChange={handleAppName}
            onSearch={handleAppNameSearch}
            useEffectDeps={[appNameSearch]}
            isDataDependent={[appNameSearch]}
            filterOption={false}
            responsePath="projectsForRelease.projects"
            valuePath="name"
            labelPath="name"
            fetchPolicy="network-only"
            uniqByVariable="name"
            onClear={() =>
              setDraftFilter((prev) => ({
                ...prev,
                appName: [],
              }))
            }
            value={draftFilter?.appName}
            maxTagCount="responsive"
            maxTagTextLength={10}
            virtual={false}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button danger size="small" onClick={() => handleReset('appName')}>
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getAppTypeFilterProps = () => ({
    filterDropdownOpen: filterVisible?.appTypeFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        appTypeFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={params?.appType?.length > 0 ? 'filter-filled-data' : ''}
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            mode="multiple"
            placeholder="Select App Type"
            options={APP_TYPES}
            value={draftFilter?.appType}
            onChange={handleAppType}
            maxTagCount="responsive"
            maxTagTextLength={10}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button danger size="small" onClick={() => handleReset('appType')}>
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getEnvironmentFilterProps = () => ({
    filterDropdownOpen: filterVisible?.environmentFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        environmentFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={
          params?.environment?.length > 0 ? 'filter-filled-data' : ''
        }
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            mode="multiple"
            placeholder="Select Environment"
            options={APP_BUILD_ENVIRONMENTS}
            value={draftFilter?.environment}
            onChange={handleEnvironment}
            maxTagCount="responsive"
            maxTagTextLength={10}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReset('environment')}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getAssigneeFilterProps = () => ({
    filterDropdownOpen: filterVisible?.assigneeFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        assigneeFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={params?.assignee?.length > 0 ? 'filter-filled-data' : ''}
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <CommonSelect
            mode="multiple"
            placeholder="Select Assignee"
            showSearch
            query={APP_BUILD_ASSIGNEES}
            onChange={handleAssignee}
            responsePath="appBuildAssignees"
            valuePath="id"
            labelPath="displayName"
            fetchPolicy="network-only"
            uniqByVariable="displayName"
            onClear={() =>
              setDraftFilter((prev) => ({
                ...prev,
                assignee: [],
              }))
            }
            value={draftFilter?.assignee}
            maxTagCount="responsive"
            maxTagTextLength={10}
            virtual={false}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button danger size="small" onClick={() => handleReset('assignee')}>
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getAndroidStatusFilterProps = () => ({
    filterDropdownOpen: filterVisible?.androidStatusFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        androidStatusFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={
          params?.androidStatus?.length > 0 ? 'filter-filled-data' : ''
        }
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            mode="multiple"
            placeholder="Select Android Status"
            options={APP_BUILD_REVIEW_STATUS}
            value={draftFilter?.androidStatus}
            onChange={handleAndroidStatus}
            maxTagCount="responsive"
            maxTagTextLength={10}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReset('androidStatus')}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const getIosStatusFilterProps = () => ({
    filterDropdownOpen: filterVisible?.iosStatusFilterVisible,
    onFilterDropdownOpenChange: (visible) =>
      setFilterVisible({
        ...filterVisible,
        iosStatusFilterVisible: visible,
      }),
    filterIcon: () => (
      <FilterFilled
        className={params?.iosStatus?.length > 0 ? 'filter-filled-data' : ''}
      />
    ),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            mode="multiple"
            placeholder="Select iOS Status"
            options={APP_BUILD_REVIEW_STATUS}
            value={draftFilter?.iosStatus}
            onChange={handleIosStatus}
            maxTagCount="responsive"
            maxTagTextLength={10}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReset('iosStatus')}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
  });

  const handleViewDetails = (record) => {
    history?.push(`${ROUTES_MODULES_KEY?.APP_BUILDS}/${record?.id}`);
  };

  const [statusUpdate] = useMutation(APP_BUILD_STATUS_UPDATE, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const handleSendStatusUpdate = (record) => {
    statusUpdate({ variables: { appBuildStatusUpdateId: record?.id } });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Request No.',
        dataIndex: 'requestNumber',
        key: 'requestNumber',
        width: '8%',
        sorter: true,
        ...getRequestNumberFilterProps(),
      },
      {
        title: 'Created At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '10%',
        sorter: true,
        render: (date) =>
          date ? moment(date).format(DEFAULT_DATE_FORMAT) : '-',
      },
      {
        title: 'App Name',
        dataIndex: 'appName',
        key: 'appName',
        width: '12%',
        sorter: true,
        ...getAppNameFilterProps(),
      },
      {
        title: 'App Type',
        dataIndex: 'appType',
        key: 'appType',
        width: '10%',
        sorter: true,
        ...getAppTypeFilterProps(),
      },
      {
        title: 'Environment',
        dataIndex: 'environment',
        key: 'environment',
        width: '12%',
        sorter: true,
        ...getEnvironmentFilterProps(),
      },
      {
        title: 'Assignee',
        dataIndex: 'assigneeName',
        key: 'assigneeName',
        width: '10%',
        sorter: true,
        ...getAssigneeFilterProps(),
      },
      {
        title: 'Android Version + Code',
        key: 'androidVersionCode',
        width: '13%',
        render: (_, record) =>
          `${record?.androidAppVersion || '-'} / ${
            record?.androidVersionCode || '-'
          }`,
      },
      {
        title: 'Android Status',
        dataIndex: 'androidStatus',
        key: 'androidStatus',
        width: '12%',
        sorter: true,
        ...getAndroidStatusFilterProps(),
        render: (status) => <StatusBadge status={status} />,
      },
      {
        title: 'iOS Version + Code',
        key: 'iosVersionCode',
        width: '10%',
        render: (_, record) =>
          `${record?.iosAppVersion || '-'} / ${record?.iosBuildNumber || '-'}`,
      },
      {
        title: 'iOS Status',
        dataIndex: 'iosStatus',
        key: 'iosStatus',
        width: '12%',
        sorter: true,
        ...getIosStatusFilterProps(),
        render: (status) => <StatusBadge status={status} />,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: '5%',
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                { key: 'viewDetails', label: 'View Details' },
                VerifyPermissions({
                  modulekey: MODULES_KEY?.APP_BUILDS,
                  allowedPermissions: [PERMISSION_OBJECT?.update],
                }) && {
                  key: 'sendStatusUpdate',
                  label: 'Send App Status Update',
                },
              ].filter(Boolean),
              onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation();
                if (key === 'viewDetails') handleViewDetails(record);
                if (key === 'sendStatusUpdate') handleSendStatusUpdate(record);
              },
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              icon={<EllipsisOutlined />}
              onClick={(e) => e?.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterVisible, params, draftFilter, appNameSearch],
  );

  const renderMaxTag = (omittedValues) => {
    if (omittedValues?.length === 0) {
      return null;
    }

    return <span>+ {omittedValues?.length} more...</span>;
  };

  const handleSearch = debounce((values) => {
    const selectedAppName = values || [];
    setDraftFilter((prev) => ({
      ...prev,
      appName: selectedAppName,
    }));
    setDesignationDataSearch('');
    setParams({ appName: selectedAppName, page: 1 });
  }, 700);

  const handleRowClick = (record) => {
    history?.push(`${ROUTES_MODULES_KEY?.APP_BUILDS}/${record?.id}`);
  };

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="App Builds"
        extra={
          <>
            <div className="project-filter-right">
              <div className="project-filter">
                <AccessControl
                  modulekey={MODULES_KEY?.APP_BUILDS}
                  allowedPermissions={[PERMISSION_OBJECT?.create]}
                >
                  <Button
                    key="1"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      history?.push({
                        pathname: `${ROUTES_MODULES_KEY?.APP_BUILDS}/create`,
                      });
                    }}
                  >
                    Create App Build
                  </Button>
                </AccessControl>
                <span className="user-select-tags">
                  <CommonSelect
                    mode="multiple"
                    placeholder="Search App Builds"
                    showSearch
                    query={PROJECT_FOR_RELEASE}
                    onChange={handleSearch}
                    onSearch={handleDesignationSearch}
                    variables={{
                      filters: {
                        sortField: 'createdOn',
                        sortOrder: SORT_BY.DESC,
                        searchString: designationDataSearch,
                      },
                    }}
                    useEffectDeps={[designationDataSearch]}
                    isDataDependent={[designationDataSearch]}
                    filterOption={false}
                    responsePath="projectsForRelease.projects"
                    valuePath="name"
                    labelPath="name"
                    fetchPolicy="network-only"
                    uniqByVariable="name"
                    onClear={() => {
                      setDraftFilter((prev) => ({
                        ...prev,
                        appName: [],
                      }));
                      setParams({ appName: [], page: 1 });
                    }}
                    value={params?.appName}
                    maxTagCount="responsive"
                    maxTagTextLength={10}
                    maxTagPlaceholder={renderMaxTag}
                    virtual={false}
                  />
                </span>
              </div>
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            columns={columns}
            data={data?.appBuilds?.data || []}
            rowKey={(obj) => obj?.id}
            loadingData={loading}
            bordered
            tableLayout="fixed"
            paginationConfig={{
              current: params?.page,
              total: data?.appBuilds?.count || 0,
              pageSize: params?.pageSize,
            }}
            onChange={onChange}
            onRow={(record) => ({ onClick: () => handleRowClick(record) })}
            rowClassName="appBuild-table-row"
          />
        </div>
      </Card>
    </div>
  );
}

export default AppBuilds;
